import { ExternalLink } from 'lucide-react';
import { Badge, Button, ButtonLink, Dialog, Input } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import {
  useArticlePresse,
  useArchiverArticle,
  useDeposerPresse,
  useModifierArticle,
  useRetirerFichierPresse,
  telechargerPresse,
  type ArticlePresente,
} from '../../api/presse.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { FormArticle } from './FormArticle.js';
import { LiensPresse } from './LiensPresse.js';
import styles from './PressePage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
export function ArticlePressePage() {
  const { id } = useParams();
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['commun']);
  const fiche = useArticlePresse(galerieSlug, id ?? '');
  if (!fiche.data)
    return (
      <p role={fiche.isError ? 'alert' : undefined}>
        {fiche.isError
          ? estApiError(fiche.error)
            ? fiche.error.message
            : t('commun:erreurReseau')
          : t('commun:chargement')}
      </p>
    );
  return <Fiche key={id} article={fiche.data.article} />;
}
function Fiche({ article }: { article: ArticlePresente }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const modifier = useModifierArticle(galerieSlug);
  const depot = useDeposerPresse(galerieSlug);
  const retirer = useRetirerFichierPresse(galerieSlug);
  const archiver = useArchiverArticle(galerieSlug);
  const [edition, setEdition] = useState(false);
  const [confirmation, setConfirmation] = useState<'archiver' | number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [telechargement, setTelechargement] = useState<number | null>(null);
  const peutEcrire = galerieALaction(galerie, 'press:write') && !article.archiveLe;
  const erreurs = (e: unknown) => setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
  return (
    <div className={styles.page} data-ui="press-detail">
      <div className={styles.actions}>
        <Link to={`/${galerieSlug}/presse`}>{t('regie:presse.retour')}</Link>
        {peutEcrire ? (
          <>
            <Button variant="secondary" onClick={() => setEdition(true)}>
              {t('regie:presse.modifier')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmation('archiver')}>
              {t('regie:presse.archiver')}
            </Button>
          </>
        ) : null}
      </div>
      <FeuillePiece
        emetteur={galerie.nom ?? galerieSlug}
        nature={t(`regie:presse.genres.${article.genre}`)}
        numero={article.titre}
        destinataire={{
          legende: t('regie:presse.champs.support'),
          nom: article.support,
          complement: article.auteur,
        }}
        meta={
          article.paruLe
            ? new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'long',
                timeZone: 'Europe/Paris',
              }).format(new Date(`${article.paruLe}T12:00:00Z`))
            : undefined
        }
        tampon={article.archiveLe ? <Badge>{t('regie:presse.archive')}</Badge> : undefined}
      >
        <p className={styles.extrait}>{article.extrait}</p>
        {article.url ? (
          <ButtonLink
            variant="secondary"
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:presse.ouvrirArticle')}
          </ButtonLink>
        ) : null}
        {article.genre === 'catalogue' ? (
          <p>
            {article.isbn ? `ISBN ${article.isbn} · ` : ''}
            {article.pages ? t('regie:presse.nombrePages', { count: article.pages }) : ''}
          </p>
        ) : null}
        <LiensPresse article={article} />
        <section className={styles.fichiers}>
          <h2>{t('regie:presse.fichiers')}</h2>
          <ul>
            {article.fichiers.map((f) => (
              <li key={f.rang}>
                <span>{f.nom}</span>
                <Button
                  variant="secondary"
                  disabled={telechargement === f.rang}
                  onClick={() => {
                    setErreur(null);
                    setTelechargement(f.rang);
                    void telechargerPresse(galerieSlug, article.id, f.rang)
                      .then((r) => ouvrirUrlSignee(r.url))
                      .catch(erreurs)
                      .finally(() => setTelechargement(null));
                  }}
                >
                  {t('regie:presse.telecharger')}
                </Button>
                <Verrouillable action="press:write" ouvert={peutEcrire}>
                  <Button variant="ghost" onClick={() => setConfirmation(f.rang)}>
                    {t('regie:presse.retirer')}
                  </Button>
                </Verrouillable>
              </li>
            ))}
          </ul>
          {peutEcrire ? (
            <Field htmlFor="presse-fichier" label={t('regie:presse.ajouterFichier')}>
              <Input
                id="presse-fichier"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                disabled={depot.isPending}
                onChange={(e) => {
                  const fichier = e.target.files?.[0];
                  e.target.value = '';
                  if (fichier) {
                    setErreur(null);
                    void depot.mutateAsync({ id: article.id, fichier }).catch(erreurs);
                  }
                }}
              />
            </Field>
          ) : null}
        </section>
      </FeuillePiece>
      {erreur ? <p role="alert">{erreur}</p> : null}
      <Dialog
        open={edition}
        onOpenChange={setEdition}
        title={t('regie:presse.modifier')}
        closeLabel={t('commun:actions.fermer')}
      >
        {edition ? (
          <FormArticle
            galerieSlug={galerieSlug}
            initial={article}
            attente={modifier.isPending}
            enregistrer={async (corps) => {
              if (Object.keys(corps).length) await modifier.mutateAsync({ id: article.id, corps });
              setEdition(false);
            }}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={confirmation !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmation(null);
        }}
        title={t(
          confirmation === 'archiver'
            ? 'regie:presse.confirmerArchive'
            : 'regie:presse.confirmerRetrait',
        )}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            disabled={retirer.isPending || archiver.isPending}
            onClick={() => {
              const demande =
                confirmation === 'archiver'
                  ? archiver.mutateAsync(article.id)
                  : typeof confirmation === 'number'
                    ? retirer.mutateAsync({ id: article.id, rang: confirmation })
                    : Promise.resolve();
              void demande.then(() => setConfirmation(null)).catch(erreurs);
            }}
          >
            {t(confirmation === 'archiver' ? 'regie:presse.archiver' : 'regie:presse.retirer')}
          </Button>
        }
      />
    </div>
  );
}
